import { Case, FolderId, TDOId, TreeObjectId } from '@cbsa-modules/universal';
import { NonEmptyArray } from 'ts-essentials';
import type { TDOForExport } from '@redact-modules/mainPage/services';
import { ContentTemplateType } from '../store';
import { FolderPathItem } from '../../helper';

export interface FetchRootFoldersQueryResponse {
  rootFolders: {
    id: FolderId;
    name: string;
    ownerId: string;
    treeObjectId: TreeObjectId;
    description: string;
    contentTemplates: {
      id: string;
      schemaId: string;
      sdoId: string;
    }[];
    childFolders: {
      count: number;
      records: {
        id: FolderId;
        name: string;
        treeObjectId: TreeObjectId;
        description: string;
        contentTemplates: ContentTemplateType[];
        parent: {
          id: FolderId;
        };
        childFolders: {
          count: number;
          records: {
            id: FolderId;
            contentTemplates: {
              id: string;
              schemaId: string;
              sdoId: string;
            }[];
          }[];
        };
        folderPath: {
          id: FolderId;
          name: string;
        }[];
      }[];
    };
    parent: {
      id: FolderId;
    };
    folderPath: {
      id: FolderId;
      name: string;
    }[];
  }[];
}
export const fetchRootFoldersQuery = `
  query listRootFolders ($limit:Int, $offset:Int) {
    rootFolders(type: cms) {
      id
      name
      ownerId
      treeObjectId
      description
      contentTemplates {
        id
        schemaId
        sdoId
      }
      childFolders(limit:$limit, offset:$offset, orderBy:[
        { field: name, direction: asc },
        { field: createdDateTime, direction: desc }
      ]){
        count
        records {
          id
          name
          treeObjectId
          description
          contentTemplates {
            id
            schemaId
            sdoId
            sdo {
              data
            }
          }
          parent {
            id
          }
          childFolders {
            count
            records {
              id
              contentTemplates {
                 id
                 schemaId
                 sdoId
              }
            }
          }
          folderPath {
            id
            name
            parent {
              id
              name
            }
          }
        }
      }
      parent {
        id
      }
      folderPath {
        id
        name
       }
    }
  }
`;

export interface FetchSubFoldersQueryResponse {
  folder: {
    id: FolderId;
    name: string;
    treeObjectId: TreeObjectId;
    description: string;
    contentTemplates: {
      id: string;
      schemaId: string;
      sdoId: string;
    }[];
    childFolders: {
      count: number;
      records: {
        id: FolderId;
        name: string;
        treeObjectId: TreeObjectId;
        description: string;
        contentTemplates: {
          id: string;
          schemaId: string;
          sdoId: string;
        }[];
        parent: {
          id: FolderId;
        };
        childFolders: {
          count: number;
          records: {
            id: FolderId;
            contentTemplates: {
              id: string;
              schemaId: string;
              sdoId: string;
            }[];
          }[];
        };
        folderPath: {
          id: FolderId;
          name: string;
        }[];
      }[];
    };
    parent: {
      id: FolderId;
    };
    folderPath: {
      id: FolderId;
      name: string;
    }[];
  };
}
export const fetchSubFoldersQuery = `
query listSubfolders($folderId:ID!, $limit:Int, $offset:Int) {
  folder(id: $folderId) {
    id
    name
    treeObjectId
    description
    contentTemplates {
      id
      schemaId
      sdoId
    }
    childFolders(limit:$limit, offset:$offset, orderBy:[
      { field: name, direction: asc },
      { field: createdDateTime, direction: desc }
    ]) {
      count
      records {
        id,
        name
        treeObjectId
        description
        contentTemplates {
          id
          schemaId
          sdoId
          sdo {
            data
          }
        }
        parent{
          id
        }
        childFolders {
          count
          records {
            id
            contentTemplates {
               id
               schemaId
               sdoId
            }
          }
        }
        folderPath {
          id
          name
          parent {
            id
            name
          }
        }
      }
    }
    parent {
      id
    }
    folderPath {
      id
      name
    }
  }
}
`;

export const createSoftDeletedFolderSdoQuery = `
  mutation insertSoftDeletedFolderSdo(
    $folderName: String!,
    $treeObjectId: ID!,
    $currentTime: String!,
    $schemaId: ID!,
    $trashRetentionDays: Int!,
    $oldFolderPath: JSONData!,
    $movedToTrashByName: String,
    $movedToTrashById: String
  ) {
    createStructuredData(input: {
      id: ""
      schemaId: $schemaId,
      data: {
        caseName: $folderName,
        status: "new",
        folderTreeObjectId: $treeObjectId,
        createdDateTime: $currentTime,
        modifiedDateTime: $currentTime,
        priority: 1,
        processingTime: 3600,
        oldFolderPath: $oldFolderPath,
        movedToTrashDateTime: $currentTime,
        movedToTrashByName: $movedToTrashByName,
        movedToTrashById: $movedToTrashById,
        trashRetentionDays: $trashRetentionDays,
        isCase: false
      }
    }) {
      id
    }
  }
`;
export interface CreateSoftDeletedSdoQueryResponse {
  createStructuredData: {
    id: string;
  };
}

export const createNewFolderQuery = `
mutation createFolder($parentId: ID!, $newFolderName: String!) {
  createFolder(input: {
    parentId: $parentId,
    name: $newFolderName,
    description: $newFolderName
  }) {
    id
    treeObjectId
  }
}
`;
export interface CreateNewFolderQueryResponse {
  createFolder: {
    id: string;
    treeObjectId: TreeObjectId;
  };
}

export const createNewFolderSdoQuery = `
mutation insertRedactCase($newFolderName: String!, $folderTreeObjectId: ID!, $currentTime: String!, $schemaId: ID!) {
  createStructuredData(input: {
    id: ""
    schemaId: $schemaId,
    data: {
      caseName: $newFolderName,
      status: "new",
      folderTreeObjectId: $folderTreeObjectId,
      createdDateTime: $currentTime,
      modifiedDateTime: $currentTime,
      priority: 1,
      processingTime: 3600
    }
  }) {
    id
    data
  }
}
`;
export interface CreateNewFolderSdoQueryResponse {
  createStructuredData: {
    id: string;
    data: Case;
  };
}

export const createNewFolderLinkQuery = `
mutation createFolderContentTemplate($schemaId: ID!, $folderId: ID!, $sdoId: ID!) {
  createFolderContentTemplate(input: {
    folderId: $folderId,
    schemaId: $schemaId,
    sdoId: $sdoId
  }) {
    id
  }
}
`;

export interface FetchCaseContentsQueryResponse {
  folder: {
    id: FolderId;
    name: string;
    treeObjectId: TreeObjectId;
    childTDOs: {
      count: number;
      records: {
        id: TDOId;
        name: string;
        thumbnailUrl: string;
        startDateTime: string;
        stopDateTime: string;
        createdDateTime: string;
        modifiedDateTime: string;
        description: string;
        status: string;
        folders: {
          folderPath: FolderPathItem[];
        }[];
        details: unknown;
        tasks: {
          records: {
            engineId: string;
            status: string;
          }[];
        };
        jobs: {
          count: number;
          records: {
            status: string;
          }[];
        };

        primaryAsset: {
          id: string;
          contentType: string;
          assetType: string;
          createdDateTime: string;
          signedUri: string;
        };
        streams: {
          protocol: string;
          uri: string;
        }[];
        redactedMediaAssets: {
          records: {
            id: string;
            name: string;
            contentType: string;
            assetType: string;
            createdDateTime: string;
            modifiedDateTime: string;
            signedUri: string;
            details: unknown;
          }[];
        };
        auditLogAssets: {
          records: {
            id: string;
            name: string;
            contentType: string;
            assetType: string;
            createdDateTime: string;
            signedUri: string;
            details: unknown;
          }[];
        };
        redactedTranscriptAssets: {
          records: {
            id: string;
            name: string;
            contentType: string;
            assetType: string;
            createdDateTime: string;
            signedUri: string;
            details: unknown;
          }[];
        };
        redactedExportAssets: {
          records: {
            id: string;
            modifiedDateTime: string;
            assetType: string;
            signedUri: string;
            details: unknown;
          }[];
        };
      }[];
    };
  };
}
export const fetchCaseContentsQuery = `
query fetchCaseMediaContents($caseId:ID!, $limit:Int, $offset:Int){
  folder(id: $caseId) {
  id
  name
  treeObjectId
  childTDOs(limit:$limit, offset:$offset) {
    count
    records {
      id
      name
      thumbnailUrl
      startDateTime
      stopDateTime
      createdDateTime
      modifiedDateTime
      description
      status
      details
      folders {
        id
        name
        folderPath {
          id
          name
          parent {
            id
            name
          }
        }
      }
      tasks {
        records {
          engineId,
          status
          # the engine that ran it is not enough on its own: speaker separation
          # is matched by category, so either vendor's engine counts
          engine {
            categoryId
          }
        }
      }
      jobs {
        count
        records {
          status
        }
      }

      primaryAsset(assetType: "media") {
        id
        contentType
        assetType
        createdDateTime
        signedUri
      }
      streams {
      protocol
      uri
    }
      redactedMediaAssets: assets(limit: 100, orderBy: createdDateTime, orderDirection: desc, assetType: ["redacted-media", "redacted-media-clip", "redacted-document"]) {
        records {
          id
          name
          contentType
          assetType
          createdDateTime
          modifiedDateTime
          signedUri
          details
          # VE-25870: shown as File Size in Download Review, so the user knows what
          # they are about to write to disk. Nullable — assets ingested without it
          # report no size rather than zero.
          fileData {
            size
          }
        }
      }
      auditLogAssets: assets(limit: 100, orderBy: createdDateTime, orderDirection: desc, assetType: ["redact-audit-log", "blur-audit-log"]) {
        records {
          id
          name
          contentType
          assetType
          createdDateTime
          signedUri
          details
        }
      }
      # VE-25870: offered as its own Download Review option, so the row needs to
      # know whether one exists and where to fetch it.
      redactedTranscriptAssets: assets(limit: 100, orderBy: createdDateTime, orderDirection: desc, assetType: ["redacted-transcript"]) {
        records {
          id
          name
          contentType
          assetType
          createdDateTime
          signedUri
          details
        }
      }
      # VE-25870: recent redaction saves, so a row can tell whether its redacted
      # output still matches the saved redactions. Counts only, no bodies. Both
      # asset types because a folder mixes media and documents.
      redactionDataAssets: assets(limit: 1, orderBy: createdDateTime, orderDirection: desc, assetType: ["redaction-media-data", "document-redaction-data"]) {
        records {
          id
          createdDateTime
          details
        }
      }
      redactedExportAssets: assets(limit: 1, orderBy: createdDateTime, orderDirection: desc, assetType: ["redact-export", "redact-case-export", "redacted-audit-log"]) {
        records {
          id
          modifiedDateTime
          assetType
          signedUri
          details
        }
      }
    }
  }
 }
}
`;
export const softDeleteSdoQuery = `
  mutation softDeleteSdo ($sdoId: ID!, $schemaId: ID!, $data: JSONData!) {
    createStructuredData(input: {
      id: $sdoId,
      schemaId: $schemaId,
      data: $data
    }) {
      id
    }
  }
`;
export const deleteSdoQuery = `
  mutation deleteSdo ($sdoId: ID!, $schemaId: ID!) {
    deleteStructuredData(input: {
      id: $sdoId,
      schemaId: $schemaId
    }) {
      id
    }
  }
`;
export interface DeleteSdoQueryResponse {
  deleteStructuredData: {
    id: string;
  };
}
export interface SoftDeleteSdoQueryResponse {
  createStructuredData: {
    id: string;
  };
}

export interface DeleteFolderOrCaseQueryResponse {
  deleteFolder: {
    id: FolderId;
    message: string;
  };
}
export const deleteFolderOrCaseQuery = `
  mutation deleteFolder($id: ID!) {
    deleteFolder(input: {
      id: $id,
      orderIndex: 0,
    }) {
      id
      message
    }
  }
`;

export interface SoftDeleteFileQueryResponse {
  updateTDO: {
    id: TDOId;
  };
}

export interface UpdateRedactionStatusQueryResponse {
  updateTDO: {
    id: TDOId;
  };
}

export const softDeleteFileQuery = `
mutation softDeleteTdo($tdoId: ID!, $details: JSONData!) {
  updateTDO(input: {
    id: $tdoId
    details: $details
  }) {
    id
  }
}
`;

export const updateRedactionStatusQuery = `
mutation updateRedactionStatus($tdoId: ID!, $details: JSONData!) {
  updateTDO(input: {
    id: $tdoId
    details: $details
  }) {
    id
    details
  }
}
`;

export interface DeleteFileQueryResponse {
  deleteTDO: {
    id: TDOId;
    message: string;
  };
}
export const deleteFileQuery = `
  mutation deleteFile($tdoId: ID!) {
    deleteTDO(id: $tdoId) {
      id
      message
    }
  }
`;

export interface DeleteOutputFileQueryResponse {
  [key: string]: {
    id: string;
  };
}
export const deleteOutputFileQuery = (tdoIds: TDOId[]) => `
  mutation deleteOutputFiles {
    ${tdoIds
      .map(
        (tdoId, index) => `deleteAsset_${index}: deleteAsset(id: "${tdoId}") {
          id
        }`
      )
      .join(',')}
  }
`;

export interface ExportFilesQueryResponse {
  createJob: {
    id: string;
    targetId: TDOId;
    target: {
      name: string;
    };
    status: string;
  };
}
export const exportFilesQuery = `
  mutation exportFiles ($caseId: ID!, $tdoId: ID!, $engineId: ID!, $clusterId: ID!) {
    createJob(input: {
      targetId: $tdoId,
      clusterId: $clusterId,
      tasks: [
        {
          engineId: $engineId,
          executionPreferences: {
            priority: -10
          },
          payload: {
            folderTreeObjectId: $caseId,
          },
        }
      ],
    }) {
      id
      targetId
      target {
        name
      }
      status
    }
  }
`;

export interface CreateExportFilesQueryResponse {
  createTDO: {
    id: string;
    name: string;
    details: unknown;
  };
}
export const createExportFilesQuery = `
  mutation createExportTdo($input: CreateTDO!) {
    createTDO(input: $input) {
      id
      name
      details
    }
  }
`;

export interface FetchExportTdoQueryResponse {
  temporalDataObject: {
    id: TDOId;
    name: string;
    details: unknown;
    assets: {
      records: {
        id: string;
        modifiedDateTime: string;
        assetType: string;
        signedUri: string;
        details: unknown;
        sourceData: {
          taskId: string;
          sourceId: string;
        };
      }[];
    };
  };
}
export const fetchExportTdoQuery = `
  query fetchExportTdo($tdoId: ID!){
    temporalDataObject(id: $tdoId) {
      id
      name
      details
        assets(assetType: ["redact-case-export", "redacted-audit-log"]) {
          records {
            id
            modifiedDateTime
            assetType
            signedUri
            details
            sourceData{
              taskId
              sourceId
            }
          }
        }
      }
    }
`;

export interface FetchJobStatusQueryResponse {
  job: {
    id: string;
    status: string;
  };
}
export const fetchJobStatusQuery = `
  query downloadAllJobStatus ($jobId: ID!) {
    job (id: $jobId) {
      id
      status
    },
  }
`;

export interface MoveFolderOrCaseQueryResponse {
  moveFolder: {
    id: FolderId;
    orderIndex: number;
    modifiedDateTime: string;
    name: string;
    parent: {
      treeObjectId: TreeObjectId;
    };
  };
}

export const moveFolderOrCaseQuery = `
  mutation moveFolder($folderId: ID!, $toFolderId: ID!, $fromFolderId: ID! ) {
    moveFolder(input: {
      rootFolderType: cms,
      folderId: $folderId,
      fromFolderId: $fromFolderId,
      toFolderId:  $toFolderId
    }) {
      id
      orderIndex,
      modifiedDateTime,
      parent {
        treeObjectId
      },
      name
    }
  }
`;

export interface FetchCaseOrFolderTreeQueryResponse {
  folder: {
    id: FolderId;
    name: string;
    treeObjectId: TreeObjectId;
    contentTemplates: {
      id: string;
      schemaId: string;
    }[];
    parent: {
      id: FolderId;
      treeObjectId: TreeObjectId;
      ownerId: string;
    };
    folderPath: {
      id: FolderId;
      name: string;
      treeObjectId: TreeObjectId;
      folderPath: {
        id: FolderId;
        name: string;
      }[];
      contentTemplates: {
        id: string;
        schemaId: string;
      }[];
    }[];
  };
}
export const fetchCaseOrFolderTreeQuery = `
  query fetchCaseOrFolderTreeQuery ($folderId: ID!) {
    folder(id: $folderId) {
      id
      name
      treeObjectId
      contentTemplates {
        id
        schemaId
      }
      parent {
        id
        treeObjectId
        ownerId
      }
      folderPath {
        id
        name
        treeObjectId
        folderPath {
          id
          name
        }
        contentTemplates {
          id
          schemaId
        }
      }
    }
  }
`;

export const fetchFolderQuery = `
  query folder($folderId: ID!) {
    folder(id: $folderId) {
      id,
      name,
      treeObjectId,
      contentTemplates {
        id,
        folderId,
        sdoId,
        schemaId
      }
    }
  }
`;

export const searchFolderByParentQuery = `
  query searchFolderByParentQuery($folderId: ID!, $limit: Int, $offset: Int, $names: [String]) {
    folder(id: $folderId) {
      id
      name
      childFolders(limit: $limit, offset: $offset, names: $names, nameMatch: contains) {
        count
        records {
          id
          name
          createdDateTime
          contentTemplates {
            id
            schemaId
            sdo {
              data
            }
          }
        }
      }
    }
  }
`;
export interface SearchFolderByParentQueryResponse {
  folder: {
    id: string;
    name: string;
    childFolders: {
      count: number;
      records: {
        id: string;
        name: string;
        createdDateTime: string;
        contentTemplates: SearchFolderRecordContentTemplate[];
      }[];
    };
  } | null;
}

export const searchFolderAndCasesQuery = `
  query searchFolderAndCasesQuery($limit: Int, $offset: Int, $names: [String]) {
    rootFolders(type: cms) {
      id
      name
      ownerId
      childFolders(limit:$limit, offset:$offset, names:$names, nameMatch:contains){
        count
        records {
          id
          name
          createdDateTime
          contentTemplates {
            id
            schemaId
            sdo {
              data
            }
          }
        }
      }
    }
  }
`;
export interface SearchFolderRecordContentTemplate {
  id: string;
  schemaId: string;
  sdo?: {
    data?: { movedToTrashDateTime?: string } | null;
  } | null;
}
export interface SearchFolderAndCasesQueryResponse {
  rootFolders: {
    id: FolderId;
    name: string;
    ownerId: string;
    childFolders: {
      count: number;
      records: {
        id: FolderId;
        name: string;
        createdDateTime: string;
        contentTemplates: SearchFolderRecordContentTemplate[];
      }[];
    };
  }[];
}

export interface FetchLegacyMediasQueryResponse {
  structuredDataObjects: {
    records: {
      id: string;
      data: any;
      modifiedDateTime: string;
      createdDateTime: string;
    }[];
  };
}
export const fetchLegacyMediasQuery = `
  query getLegacyMedias($schemaId: ID!, $limit: Int!, $offset: Int) {
    structuredDataObjects(
      schemaId: $schemaId
      limit: $limit
      offset: $offset
      orderBy: [{ field: modifiedDateTime, direction: desc }]
      owned: true
    ) {
      records {
        id
        data
        modifiedDateTime
        createdDateTime
      }
    }
  }
`;

/**
 * VE-26943: per-TDO detail for the Unassigned Media list.
 *
 * Deliberately the SAME selection set `fetchCaseContentsQuery` pulls for a
 * request's `childTDOs`, just reached by id instead of through a folder — that
 * equivalence is what lets an unassigned row render as an ordinary
 * `AssetFileType` (Redacted/Original badge, engine icons, duration, downloads,
 * Download Review and the VE-25870 staleness check all read these fields).
 * Keep the two in sync: a field added there and missed here is a column that
 * silently blanks for unassigned files only.
 *
 * Batched as aliases because there is no "TDOs by id" list query; callers chunk
 * the ids (see UNASSIGNED_TDO_DETAIL_BATCH_SIZE).
 */
export const getUnassignedTdoDetailsQuery = (tdoIds: TDOId[]) =>
  `query getUnassignedTdoDetails {
    ${tdoIds
      .map(
        (tdoId: TDOId) => `id_${tdoId}: temporalDataObject(id: ${JSON.stringify(
          tdoId
        )}) {
        id
        name
        status
        thumbnailUrl
        createdDateTime
        modifiedDateTime
        startDateTime
        stopDateTime
        details
        folders {
          id
          name
          folderPath {
            id
            name
            parent {
              id
              name
            }
          }
        }
        tasks {
          records {
            engineId
            status
            engine {
              categoryId
            }
          }
        }
        jobs {
          count
          records {
            status
          }
        }
        primaryAsset(assetType: "media") {
          id
          contentType
          assetType
          createdDateTime
          signedUri
        }
        streams {
          protocol
          uri
        }
        redactedMediaAssets: assets(limit: 100, orderBy: createdDateTime, orderDirection: desc, assetType: ["redacted-media", "redacted-media-clip", "redacted-document"]) {
          records {
            id
            name
            contentType
            assetType
            createdDateTime
            modifiedDateTime
            signedUri
            details
            fileData {
              size
            }
          }
        }
        auditLogAssets: assets(limit: 100, orderBy: createdDateTime, orderDirection: desc, assetType: ["redact-audit-log", "blur-audit-log"]) {
          records {
            id
            name
            contentType
            assetType
            createdDateTime
            signedUri
            details
          }
        }
        redactedTranscriptAssets: assets(limit: 100, orderBy: createdDateTime, orderDirection: desc, assetType: ["redacted-transcript"]) {
          records {
            id
            name
            contentType
            assetType
            createdDateTime
            signedUri
            details
          }
        }
        redactionDataAssets: assets(limit: 1, orderBy: createdDateTime, orderDirection: desc, assetType: ["redaction-media-data", "document-redaction-data"]) {
          records {
            id
            createdDateTime
            details
          }
        }
      }`
      )
      .join(',')}
  }`;

export interface FileTdoWithCaseQueryResponse {
  fileTemporalDataObject: {
    id: TDOId;
  };
}
export const fileTdoWithCaseQuery = `
  mutation fileTdo($newFolderId: ID!, $tdoId: ID!){
    fileTemporalDataObject (input: {
      tdoId: $tdoId
      folderId: $newFolderId
    }) {
      id
    }
  }
`;

export type UnfileTdoWithCaseQueryResponse = FileTdoWithCaseQueryResponse;
export const unfileTdoWithCaseQuery = `
  mutation unfileTdo($oldFolderId: ID!, $tdoId: ID!){
    unfileTemporalDataObject (input: {
      tdoId: $tdoId
      folderId: $oldFolderId
    }) {
      id
    }
  }
`;

export interface MoveTdoQueryResponse {
  moveTemporalDataObject: {
    id: TDOId;
  };
}
export const moveTdoQuery = `
mutation moveTdo($tdoId: ID!, $oldFolderId: ID!, $newFolderId: ID! ) {
  moveTemporalDataObject(input: {
    tdoId: $tdoId
    oldFolderId: $oldFolderId
    newFolderId: $newFolderId
  }) {
    id
  }
}
`;

export interface RenameFolderQueryResponse {
  updateFolder: {
    id: FolderId;
    name: string;
  };
}
export const renameFolderQuery = `
  mutation renameFolder($id: ID!, $name: String!) {
    updateFolder(
      input: {
        id: $id
        name: $name
      }
    ) {
      id
      name
    }
  }
`;

export interface SearchNameExistInFolderQueryResponse {
  folder: {
    id: FolderId;
    childFolders: {
      count: number;
      records: {
        id: FolderId;
        name: string;
        parent: {
          id: FolderId;
        };
        contentTemplates?: ContentTemplateType[];
      }[];
    };
  };
}

export const searchNameExistInFolderQuery = `
query searchNameExists($folderId: ID!, $name: String!, $limit:Int, $offset:Int ) {
    folder(id: $folderId) {
      id
      childFolders(limit:$limit , offset:$offset, names:[$name], nameMatch:exact){
        count
        records {
          id
          name
          parent{
            id
          }
          contentTemplates {
            id
            schemaId
            sdoId
            sdo {
              data
            }
          }
        }
      }
    }
  }
`;

export interface CreateRootFoldersQueryResponse {
  createRootFolders: {
    id: FolderId;
    name: string;
  }[];
}
export const createRootFoldersQuery = `
  mutation createRootFolder {
    createRootFolders(rootFolderType:cms) {
      id
      name
    }
  }
`;
export interface CreateRootFoldersResponse {
  createRootFolders: {
    id: string;
    name: string;
  }[];
}

export const findTdoLockQuery = `
  query findTdoLock($schemaId: ID!, $tdoId: ID!) {
    structuredDataObjects(
      schemaId: $schemaId
      filter: { tdoId: $tdoId }
      orderBy: {
        field: modifiedDateTime
        direction: asc
      }
    ) {
      records {
        data
      }
    }
  }
  `;

export const getFilesByCaseIdQuery = `
  query getFilesByCaseId($caseId: ID!, $limit: Int, $offset: Int) {
    folder(id:$caseId) {
      id
      childTDOs(limit:$limit, offset:$offset) {
        records {
          id
          jobs {
            records {
              status
            }
          }
        }
      }
    }
  }
  `;

export const getChildFoldersQuery = `
  query getChildFolders ($folderId: ID!, $limit: Int, $offset: Int) {
    folder(id:$folderId) {
      id
      childFolders(limit:$limit , offset:$offset){
        records {
          id
        contentTemplates {
            id
          }
        }
      }
    }
  }
`;

export const getFilesByTreeObjectIdQuery = `
query searchMedia($treeObjectId: ID!, $limit:Int, $offset:Int) {
  searchMedia(search: {
      index: ["mine"],
      select: ["veritone-file"],
      limit: $limit,
      offset: $offset,
      query: {
        operator: "and",
        conditions:[
          {
            field: "parentTreeObjectIds",
            operator: "terms",
            values: [$treeObjectId]
          }
        ]
      }
    }) {
    jsondata
  }
}
`;
export interface GetFilesByTreeObjectIdResponse {
  searchMedia: {
    jsondata: {
      results?: {
        recording: {
          recordingId: string;
        };
      }[];
    };
  };
}

export const getTdoDetailsQuery = (
  tdoIds: NonEmptyArray<TDOId>,
  // primaryAsset.contentType is the reliable file-type source (details'
  // veritoneFile.mimetype is not present on every TDO), but it costs an
  // asset lookup per TDO — opt in only where the type matters (the single-TDO
  // ID search match), not in the batched file-path calls.
  options?: { includePrimaryAsset?: boolean }
) => `query getTdosDetailsQuery {
    ${tdoIds
      .map(
        (tdoId: TDOId) => `id_${tdoId}: temporalDataObject(id: ${tdoId}) {
          id
          name
          details
          ${
            options?.includePrimaryAsset
              ? 'primaryAsset(assetType: "media") { contentType }'
              : ''
          }
          folders {
            id
            name
            contentTemplates { id }
            parent {
              id
              name
              contentTemplates { id }
              parent {
                id
                name
                contentTemplates { id }
                parent {
                  id
                  name
                  contentTemplates { id }
                  parent {
                    id
                    name
                    contentTemplates { id }
                  }
                }
              }
            }
          }
        }`
      )
      .join(',')}
  }`;

export interface FetchSubFoldersForExportQueryResponse {
  folder: {
    id: FolderId;
    name: string;
    contentTemplates: {
      id: string;
    }[];
    childFolders: {
      count: number;
      records: {
        id: FolderId;
        name: string;
        contentTemplates: {
          id: string;
        }[];
      }[];
    };
  };
}

export const fetchSubFoldersForExportQuery = `
query listSubfoldersForExport($folderId:ID!, $limit:Int, $offset:Int) {
  folder(id: $folderId) {
    id
    name
    contentTemplates {
      id
    }
    childFolders(limit:$limit, offset:$offset, orderBy:[
      { field: name, direction: asc }
    ]) {
      count
      records {
        id
        name
        contentTemplates {
          id
        }
      }
    }
  }
}
`;

export interface FetchCaseContentsForExportQueryResponse {
  folder: {
    id: FolderId;
    name: string;
    childTDOs: {
      count: number;
      records: TDOForExport[];
    };
  };
}

export const fetchCaseContentsForExportQuery = `
query fetchCaseContentsForExport($caseId:ID!, $limit:Int, $offset:Int){
  folder(id: $caseId) {
    id
    name
    childTDOs(limit:$limit, offset:$offset) {
      count
      records {
        id
        name
        startDateTime
        stopDateTime
        createdDateTime
        modifiedDateTime
        details
        folders {
          id
          name
          folderPath {
            id
            name
            parent {
              id
              name
            }
          }
        }
        redactedMediaAssets: assets(
          limit: 1,
          orderBy: createdDateTime,
          orderDirection: desc,
          assetType: ["redacted-media", "redacted-media-clip", "redacted-document"]
        ) {
          records {
            id
            name
            modifiedDateTime
            details
          }
        }
      }
    }
  }
}
`;

/**
 * The output assets a download needs, per file, fetched when the user asks to
 * download rather than when the folder opens (VE-25870). Two reasons: the verdict
 * should reflect what is true at download time — assets may have moved on since
 * the folder was opened — and browsing a folder shouldn't pay for it.
 *
 * It selects the download fields (`name`, `signedUri`, `fileData`), not only the
 * ones a verdict reads, because the caller writes these assets to disk. Judging a
 * freshly-fetched output and then downloading the URI the folder loaded with would
 * hand over an older file while reporting the newer one as current — VE-25648
 * arriving through the check built to prevent it. Audit logs come along for the
 * same reason: they are download targets too, and a re-burn rewrites them.
 */
export const fetchStalenessAssetsQuery = (tdoIds: NonEmptyArray<TDOId>) => `
  query fetchStalenessAssets {
    ${tdoIds
      .map(
        // The id arrives from a route path segment (the editor's :tdoId), which
        // nothing validates on the way in — so it is quoted as an argument and
        // stripped for the alias rather than pasted into the document text.
        // `getUnassignedTdoDetailsQuery` already quotes; this one did not.
        (tdoId: TDOId) => `id_${String(tdoId).replace(
          /[^A-Za-z0-9_]/g,
          ''
        )}: temporalDataObject(id: ${JSON.stringify(String(tdoId))}) {
          id
          # VE-28429: the row fields the EDITOR can change, so a targeted refresh
          # of the file just closed can stand in for reloading the request.
          # Neither status nor details is read back: the recorded override
          # (VE-23391) lives on status, and details carries the redaction-status
          # tags the folder view writes optimistically — a re-read of either can
          # undo a local write. Do not re-add them here without a consumer.
          name
          modifiedDateTime
          # VE-28429: the processing dot left of the filename reads
          # jobs.records[0].status (crossed with the TDO's own status for the
          # ingestion-vs-cognition wording), and it is SUPPRESSED while the
          # redact engine runs. So a row whose jobs were loaded while its
          # redaction was queued shows nothing wrong until the "Redacting" pill
          # clears — and then uncovers a stale "Cognition Queued/Pending" that
          # never goes away. Server state, not a field the folder view writes
          # locally, so re-reading it cannot undo a local write.
          jobs {
            count
            records {
              status
            }
          }
          # VE-28061: the row's "Redacting" pill reads the latest redact-engine
          # task, so a completion refresh has to bring the tasks along or the
          # row keeps showing the state it loaded with.
          tasks {
            records {
              engineId
              status
              engine {
                categoryId
              }
            }
          }
          redactedMediaAssets: assets(limit: 100, orderBy: createdDateTime, orderDirection: desc, assetType: ["redacted-media", "redacted-media-clip", "redacted-document"]) {
            records {
              id
              name
              contentType
              assetType
              createdDateTime
              modifiedDateTime
              signedUri
              details
              fileData {
                size
              }
            }
          }
          auditLogAssets: assets(limit: 100, orderBy: createdDateTime, orderDirection: desc, assetType: ["redact-audit-log", "blur-audit-log"]) {
            records {
              id
              name
              contentType
              assetType
              createdDateTime
              signedUri
              details
            }
          }
          redactedTranscriptAssets: assets(limit: 100, orderBy: createdDateTime, orderDirection: desc, assetType: ["redacted-transcript"]) {
            records {
              id
              name
              contentType
              assetType
              createdDateTime
              signedUri
              details
            }
          }
          redactionDataAssets: assets(limit: 1, orderBy: createdDateTime, orderDirection: desc, assetType: ["redaction-media-data", "document-redaction-data"]) {
            records {
              id
              createdDateTime
              details
            }
          }
          # VE-25870: a trimmed output carries no version of its own — the engine
          # omits it because the number is a position in the patch chain and a clip
          # has no place in that sequence. The selections asset the clip names
          # carries the version Redact File asked for, so the row borrows it from
          # there. Newest only, matched by the id the output names.
          redactionSelectionAssets: assets(limit: 1, orderBy: createdDateTime, orderDirection: desc, assetType: ["redaction-user-selections"]) {
            records {
              id
              details
            }
          }
        }`
      )
      .join('\n')}
  }
`;

export interface StalenessAssetsResponse {
  [aliasedTdoId: string]: {
    id: TDOId;
    name?: string;
    modifiedDateTime?: string;
    jobs?: { count?: number; records?: Array<{ status: string }> };
    tasks?: { records?: Array<{ engineId: string; status: string }> };
    redactedMediaAssets?: { records: StalenessOutputRecord[] };
    auditLogAssets?: { records: StalenessOutputRecord[] };
    redactedTranscriptAssets?: { records: StalenessOutputRecord[] };
    redactionDataAssets?: { records: StalenessAssetRecord[] };
    redactionSelectionAssets?: { records: StalenessAssetRecord[] };
  } | null;
}

export interface StalenessAssetRecord {
  id: string;
  createdDateTime?: string;
  modifiedDateTime?: string;
  details?: Record<string, unknown>;
}

/** An output asset, carrying what a download target needs as well as a verdict. */
export interface StalenessOutputRecord extends StalenessAssetRecord {
  name?: string;
  contentType?: string;
  assetType?: string;
  signedUri?: string;
  fileData?: { size?: number | null };
}

/**
 * Every `media` asset on one TDO, so a download can tell the ingested file from
 * the searchable copy the Redact Document OCR engine republished.
 *
 * Fetched at click time rather than joined onto the folder listing: a folder can
 * hold hundreds of files and only the one being downloaded needs its asset list,
 * so paying per-file on load would be paying for browsing. The same reasoning as
 * `fetchStalenessAssetsQuery`, which this sits next to.
 *
 * `sourceData.engineId` is what identifies OCR's copy and `details` carries the
 * upload path's `isOriginalMedia` marker; see `casemedia/mediaAssets.ts` for the
 * rules that read them.
 */
export const fetchMediaAssetsQuery = `
query fetchMediaAssets($id: ID!) {
  temporalDataObject(id: $id) {
    id
    assets(assetType: "media", orderBy: createdDateTime, orderDirection: desc, limit: 50) {
      records {
        id
        name
        contentType
        createdDateTime
        signedUri
        details
        sourceData {
          engineId
        }
      }
    }
    # The file in the format it was uploaded in, where one was kept — see
    # serviceCreateOriginalMediaAsset. Same round trip; absent for PDFs, for
    # anything ingested before this asset existed for its type, and for A/V
    # ingested through a path other than the Redact upload saga.
    originalFormatAssets: assets(assetType: "original-media", orderBy: createdDateTime, orderDirection: desc, limit: 10) {
      records {
        id
        name
        contentType
        createdDateTime
        signedUri
        details
      }
    }
  }
}
`;

interface MediaAssetsAssetRecord {
  id: string;
  name?: string | null;
  contentType?: string | null;
  createdDateTime?: string | null;
  signedUri?: string | null;
  details?: unknown;
  sourceData?: { engineId?: string | null } | null;
}

export interface MediaAssetsResponse {
  temporalDataObject: {
    id: TDOId;
    assets?: { records?: MediaAssetsAssetRecord[] | null } | null;
    originalFormatAssets?: {
      records?: MediaAssetsAssetRecord[] | null;
    } | null;
  } | null;
}
